import { ClbIcon } from '@celebration/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useNavigate } from 'react-router-dom';
import IRoundConfiguration from '../../../../common/interfaces/round-configuration/IRoundConfiguration';
import AuthContext from '../../../../app/contexts/auth';
import IRoundTimes from '../../interfaces/IRoundTimes';
import Input from '../../../../common/components/input';
import UpdateConfirmationModal from '../update-confirmation-modal';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import IValidationResult from '../../../../common/interfaces/IValidationResult';
import WelcomeConstants from '../../../welcome/constants';
import { SULAHUB_ROLES } from '../../../../common/constants/roles';
import RoundManagerApiService from '../../../../common/services/roundmanager-api';
import { IRoundConfigurationErrorsSchema } from '../../interfaces/IRoundConfigurationErrorsSchema';
import { timespanToSeconds } from '../../../../common/services/roundmanager-api/configurations/configurations.service';
import './loads-rounds.scss';

interface IRoundConfigData {
    penaltyRoundsQuantity: string;
    beginningTime: string;
    acceptanceDuration: string;
    defaultAcceptanceDuration: string;
    defaultConfirmationDuration: string;
    confirmationDuration2nd: string;
    confirmationDuration3rd: string;
}

interface IRoundOptions {
    id: number;
    key: string;
    value: string | string[] | undefined;
    action: () => void;
}

function LoadsRounds() {
    const LOADS_ROUNDS_FIELDS = 4;

    const { t } = useTranslation();
    const { user, isAuthorized } = AuthContext.useAuth();
    const { setToast } = useToast();
    const navigate = useNavigate();

    const emptyRoundConfigData = useMemo<IRoundConfigData>(
        () => ({
            penaltyRoundsQuantity: '0',
            beginningTime: '00:00:00',
            acceptanceDuration: '00:00:00',
            confirmationDuration: '00:00:00',
            defaultAcceptanceDuration: '00:00:00',
            defaultConfirmationDuration: '00:00:00',
            confirmationDuration2nd: '00:00:00',
            confirmationDuration3rd: '00:00:00',
        }),
        [],
    );

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(() => {
        const isEditing: boolean[] = [];

        for (let i = 0; i < LOADS_ROUNDS_FIELDS; i++) isEditing.push(false);

        return isEditing;
    });
    const allRoundConfigurations = useRef<IRoundConfiguration[]>([]);
    const [currentRoundConfiguration, setCurrentRoundConfiguration] = useState(
        {} as IRoundConfiguration,
    );
    const [displayedTimes, setDisplayedTimes] = useState({} as IRoundTimes);
    const [modalTitle, setModalTitle] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roundConfigData, setRoundConfigData] =
        useState(emptyRoundConfigData);
    const [modalContent, setModalContent] = useState('');

    const formatBeginningTime = (timeString: string): string => {
        const now = new Date();

        now.setHours(
            Number.parseInt(timeString.substring(0, 2), 10),
            Number.parseInt(timeString.substring(3, 5), 10),
            Number.parseInt(timeString.substring(6, 8), 10),
        );

        return now.toLocaleTimeString('en-US');
    };

    const validateRoundConfiguration = useCallback((): IValidationResult => {
        const result =
            RoundManagerApiService.Configurations.roundConfigurationSchema.safeParse(
                roundConfigData,
            );

        const validationResult = {
            success: result.success,
        } as IValidationResult;

        if (!result.success) {
            validationResult.errors = result.error.format();
        }

        return validationResult;
    }, [roundConfigData]);

    const getCurrentRoundConfiguration =
        useCallback((): IRoundConfiguration => {
            return allRoundConfigurations.current.find(
                r => r.country === user?.country,
            )!;
        }, [user]);

    const closeModal = useCallback(() => {
        const currentRoundConfiguration = getCurrentRoundConfiguration();

        setRoundConfigData({
            penaltyRoundsQuantity:
                currentRoundConfiguration.penaltyRoundsQuantity,
            beginningTime: currentRoundConfiguration.firstRound!.beginningTime,
            acceptanceDuration:
                currentRoundConfiguration.firstRound!.acceptanceDuration,
            defaultAcceptanceDuration:
                currentRoundConfiguration.defaultAcceptanceDuration,
            defaultConfirmationDuration:
                currentRoundConfiguration.defaultConfirmationDuration,
            confirmationDuration2nd:
                currentRoundConfiguration.confirmationDuration2nd,
            confirmationDuration3rd:
                currentRoundConfiguration.confirmationDuration3rd,
        });
        setIsModalOpen(false);
    }, [getCurrentRoundConfiguration]);

    const processValidationErrors = useCallback(
        (validationResult: IValidationResult) => {
            const errors =
                validationResult.errors as IRoundConfigurationErrorsSchema;

            Object.keys(errors).forEach(k => {
                if (k !== '_errors')
                    (errors as any)[k]._errors.forEach((err: any) =>
                        setToast('negative', t(err)),
                    );
            });
        },
        [setToast, t],
    );

    const updateRoundConfiguration = useCallback(async () => {
        const validationResult = validateRoundConfiguration();

        if (!validationResult || !validationResult.success) {
            processValidationErrors(validationResult);

            const currentRoundConfiguration = getCurrentRoundConfiguration();
            setRoundConfigData(() => ({
                penaltyRoundsQuantity:
                    currentRoundConfiguration.penaltyRoundsQuantity,
                beginningTime:
                    currentRoundConfiguration.firstRound!.beginningTime,
                acceptanceDuration:
                    currentRoundConfiguration.firstRound!.acceptanceDuration,
                defaultAcceptanceDuration:
                    currentRoundConfiguration.defaultAcceptanceDuration,
                defaultConfirmationDuration:
                    currentRoundConfiguration.defaultConfirmationDuration,
                confirmationDuration2nd:
                    currentRoundConfiguration.confirmationDuration2nd,
                confirmationDuration3rd:
                    currentRoundConfiguration.confirmationDuration3rd,
            }));
            setIsModalOpen(false);
            return;
        }

        const configIdx = allRoundConfigurations.current.findIndex(
            r => r.id === currentRoundConfiguration.id,
        );

        const currentConfig = allRoundConfigurations.current[configIdx];
        const updatedConfig: IRoundConfiguration = {
            ...currentRoundConfiguration,
            penaltyRoundsQuantity: roundConfigData.penaltyRoundsQuantity,
            defaultAcceptanceDuration:
                roundConfigData.defaultAcceptanceDuration,
            defaultConfirmationDuration:
                roundConfigData.defaultConfirmationDuration,
            confirmationDuration2nd: roundConfigData.confirmationDuration2nd,
            confirmationDuration3rd: roundConfigData.confirmationDuration3rd,
            firstRound: {
                ...currentRoundConfiguration.firstRound!,
                beginningTime: roundConfigData.beginningTime,
                acceptanceDuration: roundConfigData.acceptanceDuration,
                confirmationDuration:
                    roundConfigData.defaultConfirmationDuration,
            },
        };

        if (JSON.stringify(updatedConfig) !== JSON.stringify(currentConfig)) {
            const { response, error } =
                await RoundManagerApiService.Configurations.updateRoundConfiguration(
                    updatedConfig,
                );

            if (response) {
                setToast('positive', t('RoundConfigurationUpdated'));

                allRoundConfigurations.current[configIdx] = updatedConfig;
                setCurrentRoundConfiguration({ ...updatedConfig });
            } else {
                setToast('negative', t('Erro'), error?.message);
                setRoundConfigData(() => ({
                    penaltyRoundsQuantity:
                        currentRoundConfiguration.penaltyRoundsQuantity,
                    beginningTime:
                        currentRoundConfiguration.firstRound!.beginningTime,
                    acceptanceDuration:
                        currentRoundConfiguration.firstRound!
                            .acceptanceDuration,
                    defaultAcceptanceDuration:
                        currentRoundConfiguration.defaultAcceptanceDuration,
                    defaultConfirmationDuration:
                        currentRoundConfiguration.defaultConfirmationDuration,
                    confirmationDuration2nd:
                        currentRoundConfiguration.confirmationDuration2nd,
                    confirmationDuration3rd:
                        currentRoundConfiguration.confirmationDuration3rd,
                }));
            }
        }

        setIsModalOpen(false);
    }, [
        t,
        setToast,
        currentRoundConfiguration,
        getCurrentRoundConfiguration,
        validateRoundConfiguration,
        processValidationErrors,
        roundConfigData,
    ]);

    const formatRoundPhaseDurationTimes = useCallback(
        (phaseDuration: string): string => {
            const hours = phaseDuration.substring(0, 2);
            const minutes = phaseDuration.substring(3, 5);

            let formatedPhaseDuration = '';

            if (hours !== '00')
                formatedPhaseDuration = `${hours} ${t('FormatedHourDuration')}`;

            return `${
                formatedPhaseDuration
            }${minutes} ${t('FormatedMinuteDuration')}`;
        },
        [t],
    );

    const getFirstAcceptanceDuration = useCallback(
        (firstAcceptanceDuration: string) => {
            const hours = Number.parseInt(
                firstAcceptanceDuration.substring(0, 2),
                10,
            );
            const minutes = Number.parseInt(
                firstAcceptanceDuration.substring(3, 5),
                10,
            );

            let formatedExtendedFirstRoundDuration: string = '';

            if (hours !== 0)
                formatedExtendedFirstRoundDuration = `${hours} ${t('FormatedHourDuration')} `;

            if (minutes !== 0)
                formatedExtendedFirstRoundDuration += `${minutes} ${t('FormatedMinuteDuration')}`;

            if (formatedExtendedFirstRoundDuration !== '') {
                return formatedExtendedFirstRoundDuration;
            }

            return undefined;
        },
        [t],
    );

    const loadCountryRoundConfigurations = useCallback(async () => {
        if (allRoundConfigurations.current.length === 0) {
            const { response, error } =
                await RoundManagerApiService.Configurations.getAllRoundConfigurations();

            if (error) {
                setToast('negative', t('Erro'), error?.message);
                navigate(WelcomeConstants.default);

                return;
            }

            if (response) {
                allRoundConfigurations.current = response;
            }
        }

        const currentRoundConfig = getCurrentRoundConfiguration();

        setCurrentRoundConfiguration(currentRoundConfig);
        setRoundConfigData({
            penaltyRoundsQuantity: currentRoundConfig.penaltyRoundsQuantity,
            beginningTime: currentRoundConfig.firstRound!.beginningTime,
            acceptanceDuration:
                currentRoundConfig.firstRound!.acceptanceDuration,
            defaultAcceptanceDuration:
                currentRoundConfig.defaultAcceptanceDuration,
            defaultConfirmationDuration:
                currentRoundConfig.defaultConfirmationDuration,
            confirmationDuration2nd: currentRoundConfig.confirmationDuration2nd,
            confirmationDuration3rd: currentRoundConfig.confirmationDuration3rd,
        });
        setIsLoading(false);
    }, [t, setToast, getCurrentRoundConfiguration, navigate]);

    const secondsToTimespan = (totalSeconds: number) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return [hours, minutes, seconds]
            .map(val => String(val).padStart(2, '0'))
            .join(':');
    };

    const getConfirmationDuration1st = useCallback(() => {
        const { defaultConfirmationDuration, confirmationDuration2nd } =
            roundConfigData;

        const defaultConfirmationDurationSeconds = timespanToSeconds(
            defaultConfirmationDuration,
        );

        const confirmationDuration2ndSeconds = timespanToSeconds(
            confirmationDuration2nd,
        );

        return formatRoundPhaseDurationTimes(
            secondsToTimespan(
                defaultConfirmationDurationSeconds -
                    confirmationDuration2ndSeconds,
            ),
        );
    }, [roundConfigData, formatRoundPhaseDurationTimes]);

    useEffect(() => {
        loadCountryRoundConfigurations();
    }, [loadCountryRoundConfigurations]);

    useEffect(() => {
        const displayedTimes: IRoundTimes = {
            displayedBeginningTime: formatBeginningTime(
                roundConfigData.beginningTime,
            ),
            displayedAcceptanceDuration: formatRoundPhaseDurationTimes(
                roundConfigData.defaultAcceptanceDuration,
            ),
            displayedConfirmationDuration: formatRoundPhaseDurationTimes(
                roundConfigData.defaultConfirmationDuration,
            ),
            displayedExtendedFirstRoundDuration: getFirstAcceptanceDuration(
                roundConfigData.acceptanceDuration,
            ),
            displayedConfirmationDuration1st: getConfirmationDuration1st(),
            displayedConfirmationDuration2nd: formatRoundPhaseDurationTimes(
                roundConfigData.confirmationDuration2nd,
            ),
            displayedConfirmationDuration3rd: formatRoundPhaseDurationTimes(
                roundConfigData.confirmationDuration3rd,
            ),
        };

        setDisplayedTimes(displayedTimes);
    }, [
        roundConfigData,
        formatRoundPhaseDurationTimes,
        getFirstAcceptanceDuration,
        getConfirmationDuration1st,
    ]);

    const modalContents = useMemo(
        () => [
            'UpdatePenaltyContent',
            'UpdateBeginningTimeContent',
            'UpdateRoundDurationContent',
            'UpdateExtendedRoundContent',
        ],
        [],
    );

    const loadsRoundsOptions: IRoundOptions[] = useMemo(
        () => [
            {
                id: 0,
                key: t('NonConfirmationPenalty'),
                value: `${currentRoundConfiguration.penaltyRoundsQuantity} ${t('Rounds')}`,
                action: () => {
                    setModalTitle(t('UpdatePenalty'));
                    setModalContent(t(modalContents[0]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 1,
                key: t('RoundStartingTime'),
                value: displayedTimes.displayedBeginningTime,
                action: () => {
                    setModalTitle(t('UpdateRoundBeginningTime'));
                    setModalContent(t(modalContents[1]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 2,
                key: t('RoundDuration'),
                value: [
                    `${t('Acceptance')}: ${displayedTimes.displayedAcceptanceDuration}` +
                        ` | ${t('Confirmation')}: ${displayedTimes.displayedConfirmationDuration}`,
                    `${t('FirstPlaceConfirmation')}: ${displayedTimes.displayedConfirmationDuration1st}`,
                    `${t('SecondPlaceConfirmation')}: ${displayedTimes.displayedConfirmationDuration2nd}`,
                    `${t('ThirdPlaceConfirmation')}: ${displayedTimes.displayedConfirmationDuration3rd}`,
                ],
                action: () => {
                    setModalTitle(t('UpdateRoundDurationTime'));
                    setModalContent(t(modalContents[2]));
                    setIsModalOpen(true);
                },
            },
            {
                id: 3,
                key: t('FirstAcceptanceDuration'),
                value: displayedTimes.displayedExtendedFirstRoundDuration,
                action: () => {
                    setModalTitle(t('UpdateFirstRoundDurationTime'));
                    setModalContent(t(modalContents[3]));
                    setIsModalOpen(true);
                },
            },
        ],
        [t, currentRoundConfiguration, displayedTimes, modalContents],
    );

    return (
        <>
            <div className="preferences-inside-container">
                <div className="container-title">
                    <h1>{t('LoadsRoundsManagement')}</h1>
                </div>

                {isLoading && (
                    <div className="loading-container">
                        <ClbLoading />
                    </div>
                )}

                {!isLoading && (
                    <div className="container-options">
                        {loadsRoundsOptions.map(option => (
                            <div className="option" key={option.id}>
                                <div>
                                    {!isEditing[option.id] && (
                                        <>
                                            <span>{t(option.key)}</span>
                                            {Array.isArray(option.value) ? (
                                                option.value.map(v => (
                                                    <span key={v}>{v}</span>
                                                ))
                                            ) : (
                                                <span>{option.value}</span>
                                            )}
                                        </>
                                    )}

                                    {option.id === 0 &&
                                        isEditing[option.id] && (
                                            <Input
                                                label={t(option.key)}
                                                value={
                                                    roundConfigData.penaltyRoundsQuantity
                                                }
                                                onChange={e =>
                                                    setRoundConfigData(s => ({
                                                        ...s,
                                                        penaltyRoundsQuantity:
                                                            e.target.value,
                                                    }))
                                                }
                                                dataTestId="round-penalty-input"
                                            />
                                        )}

                                    {option.id === 1 &&
                                        isEditing[option.id] && (
                                            <Input
                                                label={t(option.key)}
                                                value={
                                                    roundConfigData.beginningTime
                                                }
                                                onChange={e =>
                                                    setRoundConfigData(s => ({
                                                        ...s,
                                                        beginningTime:
                                                            e.target.value,
                                                    }))
                                                }
                                                icon="time"
                                                dataTestId="round-beginning-input"
                                            />
                                        )}

                                    {option.id === 2 &&
                                        isEditing[option.id] && (
                                            <div className="multiple-inputs">
                                                <span>{t(option.key)}</span>
                                                <div>
                                                    <span>
                                                        {t('Acceptance')}
                                                    </span>
                                                    <Input
                                                        value={
                                                            roundConfigData.defaultAcceptanceDuration
                                                        }
                                                        onChange={e =>
                                                            setRoundConfigData(
                                                                s => ({
                                                                    ...s,
                                                                    defaultAcceptanceDuration:
                                                                        e.target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                        icon="time"
                                                        dataTestId="default-acceptance-input"
                                                    />
                                                </div>

                                                <div>
                                                    <span>
                                                        {t('Confirmation')}
                                                    </span>
                                                    <Input
                                                        value={
                                                            roundConfigData.defaultConfirmationDuration
                                                        }
                                                        onChange={e =>
                                                            setRoundConfigData(
                                                                s => ({
                                                                    ...s,
                                                                    defaultConfirmationDuration:
                                                                        e.target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                        icon="time"
                                                        dataTestId="default-confirmation-input"
                                                    />
                                                </div>

                                                <div>
                                                    <span>
                                                        {t(
                                                            'SecondPlaceConfirmation',
                                                        )}
                                                    </span>
                                                    <Input
                                                        value={
                                                            roundConfigData.confirmationDuration2nd
                                                        }
                                                        onChange={e =>
                                                            setRoundConfigData(
                                                                s => ({
                                                                    ...s,
                                                                    confirmationDuration2nd:
                                                                        e.target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                        icon="time"
                                                        dataTestId="confirmation-duration-2nd-input"
                                                    />
                                                </div>

                                                <div>
                                                    <span>
                                                        {t(
                                                            'ThirdPlaceConfirmation',
                                                        )}
                                                    </span>
                                                    <Input
                                                        value={
                                                            roundConfigData.confirmationDuration3rd
                                                        }
                                                        onChange={e =>
                                                            setRoundConfigData(
                                                                s => ({
                                                                    ...s,
                                                                    confirmationDuration3rd:
                                                                        e.target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                        icon="time"
                                                        dataTestId="confirmation-duration-3rd-input"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                    {option.id === 3 &&
                                        isEditing[option.id] && (
                                            <Input
                                                label={t(option.key)}
                                                value={
                                                    roundConfigData.acceptanceDuration
                                                }
                                                onChange={e =>
                                                    setRoundConfigData(s => ({
                                                        ...s,
                                                        acceptanceDuration:
                                                            e.target.value,
                                                    }))
                                                }
                                                icon="time"
                                                dataTestId="first-round-acceptance-duration-input"
                                            />
                                        )}
                                </div>

                                {!isAuthorized(
                                    SULAHUB_ROLES.SYSTEM_PROFILES
                                        .OPERATION_USER,
                                ) && (
                                    <button
                                        type="button"
                                        disabled={
                                            !isEditing[option.id] &&
                                            isEditing.filter(x => x).length !==
                                                0
                                        }
                                        onClick={() => {
                                            const editingField =
                                                isEditing[option.id];

                                            if (editingField) option.action();

                                            setIsEditing(s => {
                                                s[option.id] = !editingField;
                                                return [...s];
                                            });
                                        }}
                                        data-testid={`${option.id}-${isEditing[option.id] ? 'confirm' : 'edit'}-button`}
                                    >
                                        <ClbIcon
                                            icon={
                                                isEditing[option.id]
                                                    ? 'check'
                                                    : 'edit'
                                            }
                                            size="md"
                                        />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="fixed-rules-rounds">
                    <b>{t('FixedRulesTitle')}</b> <br />
                    {t('RoundsMidRoundInsertionRule')} <br />
                    {t('RoundsMidRoundInsertionNotAvailableRule')} <br />
                    {t('RoundsConfirmationByOrderRule')}
                </div>
            </div>

            {isModalOpen && (
                <UpdateConfirmationModal
                    title={modalTitle}
                    content={modalContent}
                    opened={isModalOpen}
                    closeModal={closeModal}
                    closeModalCallback={updateRoundConfiguration}
                />
            )}
        </>
    );
}

export default LoadsRounds;
